import { Injectable } from '@angular/core';
import { IntercomService } from '@app/core/services/intercom/intercom.service';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

import { ChatIdentity, ChatProvider, ChatProviderName } from './chat-provider';
import { CrispService } from './crisp/crisp.service';

/**
 * The single chat entry point for the app.
 *
 * Which vendor actually runs is decided once, at construction, by
 * `environment.chat.provider`. Nothing outside this folder should import
 * `IntercomService` or `CrispService` directly — inject `ChatService` instead,
 * so swapping vendors stays a one-line environment change.
 *
 * Both providers are injected rather than resolved through a DI factory: they
 * are inert until `initialize()` is called, and having both instantiated keeps
 * the switch a plain runtime branch that is trivial to flip while testing.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatService implements ChatProvider {
  private readonly provider: ChatProvider;
  private readonly providerName: ChatProviderName;

  constructor(
    private readonly _intercomService: IntercomService,
    private readonly _crispService: CrispService,
  ) {
    const configured = environment.chat?.provider ?? 'intercom';

    if (configured === 'crisp') {
      this.providerName = 'crisp';
      this.provider = this._crispService;
    } else {
      if (configured !== 'intercom') {
        LoggingService.warn(
          `[Chat] Unknown provider "${configured}" — falling back to Intercom`,
        );
      }
      this.providerName = 'intercom';
      this.provider = this._intercomService;
    }

    LoggingService.log(`[Chat] Using provider: ${this.providerName}`);
  }

  /** Which vendor is live — useful for provider-specific references. */
  get activeProvider(): ChatProviderName {
    return this.providerName;
  }

  get unreadCount$(): Observable<number> {
    return this.provider.unreadCount$;
  }

  initialize(user: unknown, identity?: ChatIdentity): Promise<void> {
    return this.provider.initialize(user, identity);
  }

  showMessages(): Promise<void> {
    return this.provider.showMessages();
  }

  displayMessenger(): Promise<void> {
    return this.provider.displayMessenger();
  }

  newMessage(message?: string): Promise<void> {
    return this.provider.newMessage(message);
  }

  helpCenter(): Promise<void> {
    return this.provider.helpCenter();
  }

  showArticle(articleRef: string): Promise<void> {
    return this.provider.showArticle(articleRef);
  }

  trackEvent(
    eventName: string,
    metaData?: Record<string, unknown>,
  ): Promise<void> {
    return this.provider.trackEvent(eventName, metaData);
  }

  reportAnIssue(): Promise<void> {
    return this.provider.reportAnIssue();
  }

  startSurvey(surveyId: string): void {
    this.provider.startSurvey(surveyId);
  }

  shutdown(): Promise<void> {
    return this.provider.shutdown();
  }
}
